import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";

export function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"create" | "join">("create");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [householdName, setHouseholdName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await signup({
        email,
        password,
        name,
        mode,
        household_name: mode === "create" ? householdName : undefined,
        invite_code: mode === "join" ? inviteCode.toUpperCase() : undefined,
      });
      navigate("/", { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-6 py-10">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold">Create your account</h1>
        <p className="text-sm text-stone-500 dark:text-stone-400">
          Start a household or join an existing one
        </p>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 rounded-lg bg-stone-100 p-1 dark:bg-stone-800">
        <button
          type="button"
          onClick={() => setMode("create")}
          className={`rounded-md py-2 text-sm font-medium transition ${
            mode === "create"
              ? "bg-white text-stone-900 shadow-sm dark:bg-stone-700 dark:text-stone-100"
              : "text-stone-500 dark:text-stone-400"
          }`}
        >
          New household
        </button>
        <button
          type="button"
          onClick={() => setMode("join")}
          className={`rounded-md py-2 text-sm font-medium transition ${
            mode === "join"
              ? "bg-white text-stone-900 shadow-sm dark:bg-stone-700 dark:text-stone-100"
              : "text-stone-500 dark:text-stone-400"
          }`}
        >
          Join with code
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="name">Your name</label>
          <input
            id="name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">At least 8 characters</p>
        </div>

        {mode === "create" ? (
          <div>
            <label className="label" htmlFor="hh">Household name</label>
            <input
              id="hh"
              className="input"
              placeholder="e.g. The Smiths"
              value={householdName}
              onChange={(e) => setHouseholdName(e.target.value)}
              required
            />
          </div>
        ) : (
          <div>
            <label className="label" htmlFor="code">Invite code</label>
            <input
              id="code"
              className="input uppercase tracking-widest"
              placeholder="6-character code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              maxLength={6}
              required
            />
            <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              Ask someone in your household for the code (Settings → Household).
            </p>
          </div>
        )}

        {err && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{err}</p>}

        <button type="submit" className="btn-primary w-full" disabled={submitting}>
          {submitting ? "Creating…" : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-stone-500 dark:text-stone-400">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-brand-700 hover:underline dark:text-brand-400">
          Sign in
        </Link>
      </p>
    </div>
  );
}
